import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type {
  Channel,
  PatchTeamRequest,
  TeamMember,
} from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";

export class TeamRemoveIdentityCommand extends M8tCommand {
  static paths = [["team", "remove-identity"]];
  static usage = Command.Usage({
    description: "Remove one channel identity from a team member.",
    details:
      "Exactly one of --telegram, --slack, --teams must be passed. Use 'm8t team remove <handle>' to remove the member entirely (last-identity removal is rejected).",
    examples: [
      ["Remove a member's Slack identity", "$0 team remove-identity idabest --slack"],
    ],
  });

  handle = Option.String();
  telegram = Option.Boolean("--telegram", false);
  slack = Option.Boolean("--slack", false);
  teams = Option.Boolean("--teams", false);
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const channels: Channel[] = [];
    if (this.telegram) channels.push("telegram");
    if (this.slack) channels.push("slack");
    if (this.teams) channels.push("teams");

    if (channels.length === 0) {
      throw new LocalCliError({
        code: "USAGE",
        message: "exactly one of --telegram, --slack, --teams is required.",
        hint: `Example: m8t team remove-identity ${this.handle} --slack`,
      });
    }
    if (channels.length > 1) {
      throw new LocalCliError({
        code: "USAGE",
        message: "only one channel flag at a time. To remove multiple, run the command twice.",
        hint: `m8t team remove-identity ${this.handle} --slack && m8t team remove-identity ${this.handle} --teams`,
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;

    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });
    const body: PatchTeamRequest = { removeIdentities: channels };

    const member = await apiCall<TeamMember>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      {
        method: "PATCH",
        path: `/api/team/${encodeURIComponent(this.handle)}`,
        body,
      },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(member) + "\n");
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} removed ${channels[0]} identity from ${colors.field(member.handle)}.\n`,
    );
    return 0;
  }
}
